import { useCallback, useMemo, useRef, useState } from 'react'
import Draggable from 'react-draggable'
import type { DraggableData, DraggableEvent } from 'react-draggable'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import {
  Modal as AntModal,
  Col,
  Row,
  Checkbox,
  DatePicker,
  Input,
  InputNumber,
  notification
} from 'antd'
import type { RangePickerProps } from 'antd/es/date-picker'
import dayjs from 'dayjs'
import moment from 'moment'
import styled from 'styled-components'

import { COUNTRY } from '@qflow/constants'
import { sizes, colours } from '@qflow/theme'

import { UTILITIES } from 'portal/components/Utilities/common/constants'
import { icons, Spacer, ColourButton } from 'portal/lib/primitives'
import { trackMixpanel } from 'portal/store/mixpanel/actions'
import { editReading } from 'portal/store/utilities/readings/actions'
import { getBrowserShortDateFormat } from 'portal/lib/functions/dateFormatHelpers'

import { READING_VALIDATION_MESSAGES } from './constants'
import { getClosestDatesIndices } from './functions'
import { Reading } from './Interface'
import _ from 'lodash'

type EditReadingModalProps = {
  reading: Reading
  readings: Reading[]
  selectedUtility: string
  isOpen: boolean
  onCancel: () => void
  readingsReload: () => void
}

export function EditReadingModal({
  reading,
  readings,
  selectedUtility,
  isOpen,
  onCancel,
  readingsReload
}: EditReadingModalProps) {
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 })
  const [fieldReadingDate, setFieldReadingDate] = useState<
    string | undefined | null
  >(reading.readingDateTime ? reading.readingDateTime.substring(0, 10) : null)
  const [fieldReading, setFieldReading] = useState<number | null>(
    reading.actualValue
  )
  const [fieldEstimate, setFieldEstimate] = useState<boolean>(
    reading.isEstimated
  )
  const [fieldRolledOver, setFieldRolledOver] = useState<boolean>(
    reading.hasRolledOver
  )
  const [fieldNotes, setFieldNotes] = useState<string>(reading.notes)

  const [saving, setSaving] = useState<boolean>(false)

  const [readingValidationMessage, setReadingValidationMessage] =
    useState<string>('')

  const { accountId, projectId } = useParams<{
    accountId: string
    projectId: string
  }>()

  const draggleRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()
  const projectDetails = useSelector(
    (state: any) => state.projectDetails.modules
  )

  const EV_READING_EDIT = 'frontend_utilities_readings_reading_updated'
  const trackReadingEdit = useCallback(() => {
    dispatch(
      trackMixpanel(EV_READING_EDIT, {
        project_id: projectId,
        account_id: accountId
      })
    )
  }, [accountId, dispatch, projectId])

  // Draggable modal
  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement
    const targetRect = draggleRef.current?.getBoundingClientRect()
    if (!targetRect) {
      return
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y)
    })
  }

  const { TextArea } = Input

  const showSuccessNotification = () => {
    notification.success({
      message: 'Reading successfully updated.',
      duration: 5,
      placement: 'top'
    })

    setSaving(false)
  }

  const showFailureNotification = () => {
    notification.error({
      message: 'Reading failed to be updated.',
      description: 'Please check the values and try again.',
      duration: 10,
      placement: 'top'
    })

    setSaving(false)
  }

  const modalTitle = 'Edit meter reading'

  const validateNewReading = useMemo(() => {
    // Return true if estimate is checked
    if (fieldEstimate) {
      setReadingValidationMessage('')
      return true
    }

    // Check that fieldReadingDate does not already exist in readings
    if (
      readings.some(
        reading =>
          reading.readingDateTime.substring(0, 10) ===
          fieldReadingDate?.substring(0, 10)
      )
    ) {
      setReadingValidationMessage(READING_VALIDATION_MESSAGES.DUPLICATE)
      return false
    }

    // Check that fieldReadingDate is not in the future
    if (fieldReadingDate && moment(fieldReadingDate).isAfter(moment())) {
      setReadingValidationMessage(READING_VALIDATION_MESSAGES.FUTURE_DATE)
      return false
    }

    // fieldReading is greater than the closest, non-Estimated preceeding reading in readings
    // fieldReading is less than the closest, non-Estimated postceeding reading in readings
    const filteredReadingsNoEstimated = readings.filter(
      reading => !reading.isEstimated
    )

    if (
      filteredReadingsNoEstimated.length > 0 &&
      fieldReadingDate &&
      (fieldReading || fieldReading === 0)
    ) {
      const closestDatesIndexes = getClosestDatesIndices(
        filteredReadingsNoEstimated.map(reading => reading.readingDateTime),
        fieldReadingDate
      )

      if (closestDatesIndexes.beforeIndex > -1) {
        if (
          fieldReading <
            filteredReadingsNoEstimated[closestDatesIndexes.beforeIndex]
              .actualValue &&
          !fieldRolledOver
        ) {
          setReadingValidationMessage(
            `${READING_VALIDATION_MESSAGES.TOO_LOW} ${_.round(filteredReadingsNoEstimated[closestDatesIndexes.beforeIndex].actualValue, 3)}.`
          )
          return false
        }
      }

      if (closestDatesIndexes.afterIndex > -1) {
        if (
          fieldReading >
            filteredReadingsNoEstimated[closestDatesIndexes.afterIndex]
              .actualValue &&
          !filteredReadingsNoEstimated[closestDatesIndexes.afterIndex]
            .hasRolledOver
        ) {
          setReadingValidationMessage(
            `${READING_VALIDATION_MESSAGES.TOO_HIGH} ${_.round(filteredReadingsNoEstimated[closestDatesIndexes.afterIndex].actualValue)}.`
          )
          return false
        }
      }
    }

    setReadingValidationMessage('')
    return true
  }, [fieldEstimate, fieldReading, fieldReadingDate, fieldRolledOver, readings])

  const disabledDate: RangePickerProps['disabledDate'] = current => {
    // Can not select days before today and today
    return current && current < moment('1999-12-31').endOf('day')
  }

  const saveDisabled = useMemo(() => {
    return (
      !fieldReadingDate ||
      fieldReading === undefined ||
      fieldReading === null ||
      fieldReading < 0 ||
      !validateNewReading ||
      saving
    )
  }, [fieldReadingDate, fieldReading, validateNewReading, saving])

  const handleEditReading = () => {
    if (!accountId || !projectId) {
      console.error('Account ID or Project ID is missing')
      return
    }
    dispatch(
      editReading(
        accountId,
        projectId,
        reading.readingId as string,
        reading.eTag as string,
        reading.meterId,
        fieldReadingDate as string,
        fieldReading as number,
        fieldNotes,
        fieldEstimate,
        fieldRolledOver,
        readingsReload,
        showSuccessNotification,
        showFailureNotification
      )
    )
    trackReadingEdit()
  }

  return (
    <AntModal
      title={<StyledTitle>{modalTitle}</StyledTitle>}
      open={isOpen}
      centered
      closeIcon={<icons.Cross colour={colours.BLACK} size={sizes.SMALL} />}
      onCancel={onCancel}
      maskClosable={false}
      footer={null}
      width={416}
      wrapClassName="meter-modal"
      modalRender={modal => (
        <Draggable
          bounds={bounds}
          onStart={(event, uiData) => onStart(event, uiData)}
          cancel="#readingDate, #reading, #estimate, #rolledOver, #notes"
        >
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
      transitionName=""
    >
      <Fields>
        <FieldWrapper>
          <StyledLabel htmlFor="readingDate">Reading date</StyledLabel>
          <DatePicker
            name="readingDate"
            id="readingDate"
            format={getBrowserShortDateFormat()}
            defaultValue={dayjs(fieldReadingDate, 'YYYY-MM-DD')}
            disabledDate={disabledDate}
            onChange={value => setFieldReadingDate(value?.format('YYYY-MM-DD'))}
          />
        </FieldWrapper>

        <FieldWrapper>
          <StyledLabel htmlFor="reading">
            Reading (
            {selectedUtility === UTILITIES.WATER ? (
              projectDetails?.country === COUNTRY.UK ? (
                <span>
                  m<sup>3</sup>
                </span>
              ) : (
                <span>gal</span>
              )
            ) : (
              'kWh'
            )}
            )
          </StyledLabel>
          <InputNumber
            name="reading"
            id="reading"
            type="number"
            controls={false}
            style={{ width: '100%' }}
            value={fieldReading}
            onChange={value => setFieldReading(value)}
          />
        </FieldWrapper>
      </Fields>
      <Fields>
        <FieldWrapper>
          <StyledLabel htmlFor="estimate">Estimate</StyledLabel>
          <StyledCheckbox
            name="estimate"
            id="estimate"
            checked={fieldEstimate}
            onChange={event => setFieldEstimate(event.target.checked)}
          >
            Yes
          </StyledCheckbox>
        </FieldWrapper>
        <FieldWrapper style={{ display: 'none' }}>
          <StyledLabel htmlFor="rolledOver">Rolled over</StyledLabel>
          <StyledCheckbox
            name="rolledOver"
            id="rolledOver"
            checked={fieldRolledOver}
            disabled={readings.length === 0}
            onChange={event => setFieldRolledOver(event.target.checked)}
          >
            Yes
          </StyledCheckbox>
        </FieldWrapper>
      </Fields>
      <Row style={{ marginBottom: '10px' }}>
        <Col flex="auto">
          <StyledLabel htmlFor="notes">
            Notes <Fade>(optional)</Fade>
          </StyledLabel>
          <TextArea
            name="notes"
            id="notes"
            rows={2}
            value={fieldNotes}
            onChange={event => setFieldNotes(event.target.value)}
            maxLength={200}
          />
        </Col>
      </Row>
      {readingValidationMessage && (
        <Row style={{ marginBottom: '10px' }}>
          <ErrorContainer>
            <icons.CrossCircle size={sizes.SMALL_MEDIUM} colour={colours.RED} />
            <div>{readingValidationMessage}</div>
          </ErrorContainer>
        </Row>
      )}
      <Row style={{ justifyContent: 'flex-end', marginTop: '1.5rem' }}>
        <Col>
          <ColourButton.Basic onClick={onCancel}>Cancel</ColourButton.Basic>
        </Col>
        <Spacer.Tiny />
        <Col>
          <ColourButton.Action
            onClick={handleEditReading}
            disabled={saveDisabled}
          >
            Update reading
          </ColourButton.Action>
        </Col>
      </Row>
    </AntModal>
  )
}

const StyledTitle = styled.div`
  width: 100%;
  cursor: 'move';
`
const Fields = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 1rem;
  width: 100%;
  margin-bottom: 10px;
`
const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  flex-basis: 50%;
`
const StyledLabel = styled.label`
  display: inline-block;
  margin-bottom: 0.5rem;
`
const Fade = styled.span`
  color: ${colours.CHARCOAL_GREY};
  font-size: 0.75rem;
`
const StyledCheckbox = styled(Checkbox)`
  margin-top: 0;
`
const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  color: ${colours.CORE_NAVY};
  background-color: ${colours.RED}11;
  border-radius: 5px;
  border: 1px solid ${colours.RED}22;
  padding: 0.25rem 1rem 0.25rem 0.75rem;
  width: 100%;
`
